import {
  getSelectedPatternStyle,
  getSelectedStrokeStyle,
} from 'features/mapping/mapping.utils';
import { Layer, LayerProps } from 'shared/map-exports';
import { useMapDrawerEntityForPoiState } from 'state/useMapDrawerEntityForPoiState';
import { ExternalFeatureLayerProps } from './ExternalFeatureLayer.types';
import { getLayerBeforeId } from './ExternalFeatureLayer.constants';
import {
  getLayerMetadata,
  getSelectedLayerId,
} from './ExternalFeatureLayer.utils';

type PolygonLayersProps = ExternalFeatureLayerProps;

/**
 * Renders whatever layers are defined in the `displayStyleData` of the feature layer, and:
 * - Adds a selected fill layer and a selected stroke layer that highlight the currently selected polygon.
 * - Uses the `clickListenerLayerId` to handle click events on the polygons.
 */
export const EmbeddedStylePolygonLayer = (
  props: PolygonLayersProps,
): JSX.Element => {
  const { sourceId, featureLayer, interactive } = props;
  const { shortName, displayStyleData } = featureLayer;
  const { mapDrawerEntityForPoi } = useMapDrawerEntityForPoiState();

  if (displayStyleData.displayStyleType !== 'embedded') {
    throw new Error(
      'EmbeddedStyle*Layer requires displayStyleType to be embedded',
    );
  }

  const selectedId = getSelectedLayerId(sourceId, mapDrawerEntityForPoi);

  const selectedFillStyle = getSelectedPatternStyle({
    idKey: ['id'],
    otherLayerProps: {
      source: sourceId,
      'source-layer': shortName,
    },
    selectedId,
  });

  const selectedStrokeStyle = getSelectedStrokeStyle({
    idKey: ['id'],
    otherLayerProps: {
      source: sourceId,
      'source-layer': shortName,
      paint: {
        'line-width': ['interpolate', ['linear'], ['zoom'], 8, 3, 18, 6],
      },
    },
    selectedId,
  });

  const metadata = getLayerMetadata(featureLayer, interactive);

  return (
    <>
      {displayStyleData.layers
        // Add the `source` property to each layer's style so it references the correct
        // <Source>.
        .map(
          (layer) => ({ ...layer, source: sourceId, metadata }) as LayerProps,
        )
        .map((layer) => (
          <Layer
            key={layer.id}
            {...layer}
            beforeId={getLayerBeforeId(featureLayer)}
          />
        ))}

      <Layer {...selectedFillStyle} />
      <Layer {...selectedStrokeStyle} />
    </>
  );
};
